import React, { useState, useEffect } from 'react';
import { uploadExcel, startImageUpload, getClients } from './api';

const Gmb = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [message, setMessage] = useState('');
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async () => {
        try {
            setLoading(true);
            const response = await getClients();
            setClients(response.data);
            setMessage('Clients loaded successfully.');
        } catch (error) {
            console.error('Error fetching clients:', error);
            setMessage('Failed to load clients. ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setMessage('');
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setMessage('Please select an Excel file first!');
            return;
        }

        setLoading(true);
        setMessage('Uploading and processing Excel file...');
        try {
            const response = await uploadExcel(selectedFile);
            setMessage(response.data.message);
            setSelectedFile(null); // Clear selected file
            fetchClients(); // Refresh client list
        } catch (error) {
            console.error('Error uploading Excel:', error);
            setMessage('Upload failed: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleStartUploadForClient = async (clientId) => {
        setLoading(true);
        setMessage(`Starting GMB post upload for client ID: ${clientId}...`);
        try {
            const response = await startImageUpload(clientId);
            setMessage(response.data.message);
            // Refresh clients after a delay to show progress
            setTimeout(fetchClients, 5000); 
        } catch (error) {
            console.error('Error starting GMB post upload:', error);
            setMessage('GMB post upload failed: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleStartUploadForAll = async () => {
        setLoading(true);
        setMessage('Starting GMB post upload for all pending clients...');
        try {
            const response = await startImageUpload();
            setMessage(response.data.message);
            setTimeout(fetchClients, 5000);
        } catch (error) {
            console.error('Error starting bulk GMB post upload:', error);
            setMessage('Bulk GMB post upload failed: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const getStatusClasses = (status) => {
        switch (status) {
            case 'in_progress':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'completed':
                return 'bg-green-100 text-green-800 border-green-300';
            case 'failed':
                return 'bg-red-100 text-red-800 border-red-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 py-10">
            <div className="container mx-auto px-4">
                <header className="text-center mb-10">
                    <h1 className="text-4xl font-extrabold text-gray-900">Google My Business Post Uploader</h1>
                    <p className="mt-2 text-lg text-gray-600">Automate your GMB image and post uploads with a simple tool.</p>
                </header>

                {message && (
                    <div className={`p-4 mb-6 rounded-lg shadow-sm font-medium ${loading ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {message}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Upload Card */}
                    <div className="bg-white p-6 rounded-xl shadow-lg transform transition-transform hover:scale-105">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Upload Client Data (Excel)</h2>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={handleFileChange}
                            className="w-full text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                        />
                        <button
                            onClick={handleUpload}
                            disabled={!selectedFile || loading}
                            className="w-full mt-4 py-2 px-4 rounded-full font-bold text-white transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ backgroundColor: selectedFile ? '#4F46E5' : '#818CF8' }}
                        >
                            {loading && selectedFile ? 'Uploading...' : 'Upload Excel & Process Clients'}
                        </button>
                    </div>

                    {/* Management Card */}
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Manage Client Post Uploads</h2>
                        <button
                            onClick={handleStartUploadForAll}
                            disabled={loading}
                            className="w-full py-3 px-6 rounded-full font-bold text-white bg-purple-600 hover:bg-purple-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Processing All...' : 'Start Upload for ALL Pending Clients'}
                        </button>

                        <h3 className="text-xl font-bold text-gray-700 mt-6 mb-4">Clients:</h3>
                        {loading && clients.length === 0 ? <p className="text-center text-gray-500">Loading clients...</p> : (
                            <ul className="space-y-4">
                                {clients.length === 0 ? (
                                    <p className="text-center text-gray-500">No clients found. Upload an Excel sheet.</p>
                                ) : (
                                    clients.map((client) => (
                                        <li key={client._id} className={`p-4 rounded-lg shadow-md transition-shadow hover:shadow-lg ${getStatusClasses(client.uploadStatus)}`}>
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="text-lg font-semibold">{client.clientName}</h4>
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClasses(client.uploadStatus)}`}>
                                                    {client.uploadStatus.replace('_', ' ')}
                                                </span>
                                            </div>
                                            <p className="text-sm">GMB Location ID: {client.gmbLocationId}</p>
                                            <button
                                                onClick={() => handleStartUploadForClient(client._id)}
                                                disabled={loading || client.uploadStatus === 'in_progress'}
                                                className="mt-3 w-full py-2 px-4 rounded-lg text-sm font-semibold transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                style={{ backgroundColor: client.uploadStatus === 'completed' ? '#4B5563' : '#10B981', color: '#fff' }}
                                            >
                                                {loading && client.uploadStatus === 'in_progress' ? 'Uploading...' : 'Upload Posts for This Client'}
                                            </button>
                                            {client.uploadLogs && client.uploadLogs.length > 0 && (
                                                <div className="mt-4 border-t border-gray-200 pt-4">
                                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Upload Logs:</h5>
                                                    <ul className="space-y-1 text-xs text-gray-600">
                                                        {client.uploadLogs.map((log, index) => (
                                                            <li key={index} className={`p-2 rounded ${log.status === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
                                                                [{new Date(log.timestamp).toLocaleTimeString()}] {log.message}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </li>
                                    ))
                                )}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Gmb;